import { useCallback, useRef } from "react";

/**
 * Devolve o foco a quem abriu um painel, um menu ou a ajuda quando ele fecha.
 *
 * Sem isso, quem usa o teclado fecha o painel do agente com Esc e o foco cai no `<body>`: o
 * próximo Tab recomeça do topo da página, e o botão que a pessoa tinha acabado de apertar
 * fica a dezenas de paradas de distância (WCAG 2.4.3, ordem do foco).
 *
 * `lembrar` guarda o elemento focado no instante em que o painel abre — o clique do botão já
 * o focou —, e `devolver` o foca de novo ao fechar. Quem abriu e sumiu da tela (o item do menu
 * do celular, que fecha junto) não recebe nada. O navegador que não foca o botão ao clicar
 * (o Safari) deixa o `<body>` ativo, e então também não há o que devolver.
 */
export function useFocoDeVolta() {
  const origem = useRef<HTMLElement | null>(null);

  const lembrar = useCallback(() => {
    const ativo = document.activeElement;
    origem.current =
      ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
  }, []);

  const devolver = useCallback(() => {
    const alvo = origem.current;
    origem.current = null;
    if (alvo?.isConnected) alvo.focus();
  }, []);

  return { lembrar, devolver };
}
